// Form state types for instance management
import ThirdPartyLinksType from '../ThirsPartyLinks';
import Tags from '../Tags';

// Form data interface
export interface InstanceFormData {
    id: number;
    name: string;
    host: string;
    type: string;
    agentId: number;
    database: string;
    server: string;
    debug: boolean;
    port: number;
    username: string;
    password: string;
    passwordChanged: boolean;
    advanced: boolean;
    driver: string | undefined;
    url: string;
    filter: string;
    mappedHosts: number[];
    parameters: string | undefined;
    monitoringInterval: number;
    explainLimit: number;
    batchInterval: number;
    detectChanges: boolean;
    objectStatistics: boolean;
    collectSqlStatistics: boolean;
    collectDeadlocks: boolean;
    collectTempdb: boolean;
    collectJobHistory: boolean;
    collectBlockingSessions: boolean;
    collectTransactionContention: boolean;
    monitor: boolean;
    encrypted: boolean;
    links: ThirdPartyLinksType[];
    tags: Tags[];
}

// Form validation state
export interface InstanceFormValidation {
    name: {
        isValid: boolean;
        isUsed: boolean;
        className: string;
    };
    host: {
        isValid: boolean;
        isUsed: boolean;
        className: string;
    };
    type: {
        isValid: boolean;
        className: string;
    };
    agentId: {
        isValid: boolean;
        className: string;
    };
    port: {
        isValid: boolean;
        className: string;
    };
    url: {
        isValid: boolean;
        className: string;
    };
    username: {
        className: string;
    };
    password: {
        className: string;
        elementType: 'password' | 'text';
    };
    isFormValid: boolean;
}

// Form UI state
export interface InstanceFormUIState {
    isMissing: boolean;
    disabled: boolean;
    loading: boolean;
}

// Form dependencies data
export interface InstanceFormDependencies {
    agents: any[];
    defaultValues: any[];
    initialLinks: any[];
    initialTags: any[];
    allTags: Tags[];
}

// Complete form state
export interface InstanceFormState {
    formData: InstanceFormData;
    validation: InstanceFormValidation;
    uiState: InstanceFormUIState;
    dependencies: InstanceFormDependencies;
}

// Default values
export const createDefaultFormData = (): InstanceFormData => ({
    id: 0,
    name: '',
    host: '',
    type: '',
    agentId: 1,
    database: '',
    server: '',
    debug: false,
    port: 0,
    username: '',
    password: '',
    passwordChanged: false,
    advanced: false,
    driver: undefined,
    url: '',
    filter: '',
    mappedHosts: [],
    parameters: undefined,
    monitoringInterval: 1000,
    explainLimit: 20,
    batchInterval: 60,
    detectChanges: true,
    objectStatistics: false,
    collectSqlStatistics: true,
    collectDeadlocks: false,
    collectTempdb: false,
    collectJobHistory: false,
    collectBlockingSessions: false,
    collectTransactionContention: false,
    monitor: false,
    encrypted: false,
    links: [],
    tags: []
});

export const createDefaultValidation = (): InstanceFormValidation => ({
    name: {
        isValid: true,
        isUsed: false,
        className: ''
    },
    host: {
        isValid: true,
        isUsed: false,
        className: ''
    },
    type: {
        isValid: true,
        className: ''
    },
    agentId: {
        isValid: true,
        className: ''
    },
    port: {
        isValid: true,
        className: ''
    },
    url: {
        isValid: true,
        className: ''
    },
    username: {
        className: ''
    },
    password: {
        className: '',
        elementType: 'password'
    },
    isFormValid: true
});

export const createDefaultUIState = (disabled: boolean = false): InstanceFormUIState => ({
    isMissing: false,
    disabled,
    loading: false
});

export const createDefaultDependencies = (): InstanceFormDependencies => ({
    agents: [],
    defaultValues: [],
    initialLinks: [],
    initialTags: [],
    allTags: []
});
